/**
 * ตัวสร้าง QR — เขียนเองทั้งหมด ไม่มี dependency
 *
 * ★ ทำไมไม่ใช้ไลบรารีสำเร็จรูป ★
 * QR พร้อมเพย์ต้องพิมพ์ออกสลิปได้แม้เน็ตร้านหลุด และเครื่อง Sunmi
 * จะโหลดหน้าเว็บจากแคชของ service worker เพราะฉะนั้นทุกอย่างที่ใช้ตอนปิดบิล
 * ต้องอยู่ในบันเดิลเดียวกัน ไม่พึ่ง CDN
 *
 * ขอบเขตที่ตั้งใจจำกัดไว้
 *   · โหมด byte เท่านั้น        payload พร้อมเพย์เป็น ASCII ล้วน
 *   · ระดับแก้ข้อผิดพลาด M      มาตรฐาน EMVCo ใช้ระดับนี้
 *   · เวอร์ชัน 1–10             พร้อมเพย์ยาวราว 100 ตัวอักษร ใช้เวอร์ชัน 6–7
 *
 * ตารางค่าคงที่ทั้งหมดในไฟล์นี้มี test ที่ตรวจสอบกันเองอยู่
 * โดยเทียบจำนวน codeword ที่ตารางบอก กับจำนวนที่คำนวณได้จากพื้นที่จริง
 * บนตารางโมดูล — ถ้าพิมพ์ตัวเลขผิดสักตัว test จะจับได้ทันที
 */

/* ══ เลขคณิตบนสนามจำกัด GF(256) ═══════════════════════════════
   พหุนามลดทอน 0x11D ตามที่มาตรฐาน QR กำหนด                     */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  // ต่อท้ายอีกชุดเพื่อให้บวกเลขชี้กำลังแล้วไม่ต้อง mod 255 ทุกครั้ง
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/**
 * พหุนามสร้างรหัสแก้ข้อผิดพลาด ดีกรี n — (x−α⁰)(x−α¹)…(x−αⁿ⁻¹)
 *
 * ★ เรียงสัมประสิทธิ์จากกำลังสูงไปต่ำ ★ ตัวแรกเป็น 1 เสมอ
 * ลำดับนี้ต้องตรงกับที่ rsRemainder ใช้หาร ถ้ากลับด้านกันจะได้เศษที่
 * "หน้าตาเหมือนใช้ได้" แต่เครื่องสแกนอ่านไม่ออก
 */
export function rsGenerator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j]! ^ poly[j]!) as number;
      next[j + 1] = (next[j + 1]! ^ gfMul(poly[j]!, EXP[i]!)) as number;
    }
    poly = next;
  }
  return poly;
}

/** codeword แก้ข้อผิดพลาดของบล็อกหนึ่ง = เศษจากการหารด้วยพหุนามสร้าง */
export function rsRemainder(data: Uint8Array, ecCount: number): Uint8Array {
  const gen = rsGenerator(ecCount);
  const out = new Uint8Array(ecCount);

  for (const byte of data) {
    const factor = byte ^ out[0]!;
    out.copyWithin(0, 1);
    out[ecCount - 1] = 0;
    for (let i = 0; i < ecCount; i++) {
      out[i] = (out[i]! ^ gfMul(gen[i + 1]!, factor)) as number;
    }
  }
  return out;
}

/* ══ ตารางโครงสร้างบล็อก — ระดับ M เวอร์ชัน 1–10 ══════════════ */

interface BlockSpec {
  /** codeword แก้ข้อผิดพลาดต่อหนึ่งบล็อก */
  readonly ec: number;
  /** [จำนวนบล็อก, codeword ข้อมูลต่อบล็อก] — กลุ่มที่สองอาจไม่มี */
  readonly groups: ReadonlyArray<readonly [number, number]>;
}

const SPEC_M: readonly BlockSpec[] = [
  { ec: 10, groups: [[1, 16]] },
  { ec: 16, groups: [[1, 28]] },
  { ec: 26, groups: [[1, 44]] },
  { ec: 18, groups: [[2, 32]] },
  { ec: 24, groups: [[2, 43]] },
  { ec: 16, groups: [[4, 27]] },
  { ec: 18, groups: [[4, 31]] },
  { ec: 22, groups: [[2, 38], [2, 39]] },
  { ec: 22, groups: [[3, 36], [2, 37]] },
  { ec: 26, groups: [[4, 43], [1, 44]] },
];

export const MAX_VERSION = SPEC_M.length;

/** จุดกึ่งกลางของลายจัดตำแหน่ง — ดัชนีคือเวอร์ชัน เวอร์ชัน 1 ไม่มีลายนี้ */
const ALIGNMENT: readonly (readonly number[])[] = [
  [], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
  [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

function dataCapacity(version: number): number {
  const spec = SPEC_M[version - 1]!;
  return spec.groups.reduce((sum, [count, size]) => sum + count * size, 0);
}

/* ══ ข้อมูลรูปแบบและเวอร์ชัน (รหัส BCH) ═══════════════════════
   คำนวณสด ไม่ฝังตารางไว้ — ตารางที่พิมพ์ผิดสักบิตทำให้เครื่องอ่าน
   ตีความระดับแก้ข้อผิดพลาดผิด แล้วอ่าน QR ไม่ออกทั้งใบ           */

function bch(value: number, generator: number, bits: number): number {
  let remainder = value << bits;
  const genBits = 32 - Math.clz32(generator);
  while (32 - Math.clz32(remainder) >= genBits) {
    remainder ^= generator << (32 - Math.clz32(remainder) - genBits);
  }
  return remainder;
}

/** 15 บิต — ระดับ M คือ 0b00 ในลำดับที่มาตรฐานกำหนด */
export function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask;
  return ((data << 10) | bch(data, 0x537, 10)) ^ 0x5412;
}

/** 18 บิต — ใช้เฉพาะเวอร์ชัน 7 ขึ้นไป */
export function versionBits(version: number): number {
  return (version << 12) | bch(version, 0x1f25, 12);
}

/* ══ ตารางโมดูล ═══════════════════════════════════════════════ */

export interface QrMatrix {
  readonly version: number;
  readonly size: number;
  /** true = จุดดำ · เข้าถึงแบบ modules[y][x] */
  readonly modules: readonly boolean[][];
}

/** ตารางช่วยระหว่างสร้าง — เก็บว่าโมดูลไหนถูกจองไว้เป็นลายประจำตำแหน่ง */
interface Grid {
  size: number;
  dark: boolean[][];
  reserved: boolean[][];
}

function newGrid(version: number): Grid {
  const size = version * 4 + 17;
  const make = () => Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  return { size, dark: make(), reserved: make() };
}

function set(g: Grid, x: number, y: number, dark: boolean): void {
  g.dark[y]![x] = dark;
  g.reserved[y]![x] = true;
}

function drawFinder(g: Grid, cx: number, cy: number): void {
  // วาดครอบคลุม 9×9 เพื่อให้ได้แถบคั่นสีขาวรอบลายไปในตัว
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= g.size || y >= g.size) continue;
      const ring = Math.max(Math.abs(dx - 3), Math.abs(dy - 3));
      set(g, x, y, ring !== 2 && ring <= 3);
    }
  }
}

function drawFunctionPatterns(g: Grid, version: number): void {
  drawFinder(g, 0, 0);
  drawFinder(g, g.size - 7, 0);
  drawFinder(g, 0, g.size - 7);

  // เส้นจังหวะ — สลับดำขาวเชื่อมระหว่างลายค้นหา
  for (let i = 8; i < g.size - 8; i++) {
    const dark = i % 2 === 0;
    set(g, i, 6, dark);
    set(g, 6, i, dark);
  }

  // ลายจัดตำแหน่ง — ข้ามตำแหน่งที่ทับลายค้นหา
  const centers = ALIGNMENT[version]!;
  for (const cy of centers) {
    for (const cx of centers) {
      const nearFinder =
        (cx === 6 && cy === 6) ||
        (cx === 6 && cy === g.size - 7) ||
        (cx === g.size - 7 && cy === 6);
      if (nearFinder) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          set(g, cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  // จองพื้นที่ข้อมูลรูปแบบ — ค่าจริงเติมทีหลังเมื่อรู้หน้ากากแล้ว
  for (let i = 0; i < 9; i++) {
    if (i !== 6) {
      set(g, i, 8, false);
      set(g, 8, i, false);
    }
  }
  for (let i = 0; i < 8; i++) {
    set(g, g.size - 1 - i, 8, false);
    set(g, 8, g.size - 1 - i, false);
  }
  set(g, 8, g.size - 8, true); // โมดูลดำถาวรที่มาตรฐานบังคับ

  if (version >= 7) {
    for (let i = 0; i < 18; i++) {
      const x = i % 3;
      const y = Math.floor(i / 3);
      set(g, g.size - 11 + x, y, false);
      set(g, y, g.size - 11 + x, false);
    }
  }
}

function drawFormat(g: Grid, mask: number): void {
  const bits = formatBits(mask);
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1;
    // สำเนาที่หนึ่ง — รอบลายค้นหามุมซ้ายบน
    if (i < 6) g.dark[8]![i] = dark;
    else if (i === 6) g.dark[8]![7] = dark;
    else if (i === 7) g.dark[8]![8] = dark;
    else if (i === 8) g.dark[7]![8] = dark;
    else g.dark[14 - i]![8] = dark;

    // สำเนาที่สอง — แยกไว้คนละมุมเผื่อมุมหนึ่งเสียหาย
    if (i < 8) g.dark[8]![g.size - 1 - i] = dark;
    else g.dark[g.size - 15 + i]![8] = dark;
  }
}

function drawVersion(g: Grid, version: number): void {
  if (version < 7) return;
  const bits = versionBits(version);
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >> i) & 1) === 1;
    const x = i % 3;
    const y = Math.floor(i / 3);
    g.dark[y]![g.size - 11 + x] = dark;
    g.dark[g.size - 11 + x]![y] = dark;
  }
}

/* ══ การเรียงบิตลงตาราง ═══════════════════════════════════════ */

function placeData(g: Grid, data: Uint8Array): void {
  let bit = 0;
  let upward = true;

  // เดินเป็นคู่คอลัมน์จากขวาไปซ้าย · คอลัมน์ 6 เป็นเส้นจังหวะ ต้องข้าม
  for (let right = g.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;

    for (let step = 0; step < g.size; step++) {
      const y = upward ? g.size - 1 - step : step;
      for (const x of [right, right - 1]) {
        if (g.reserved[y]![x]) continue;
        const byte = data[bit >> 3] ?? 0;
        g.dark[y]![x] = ((byte >> (7 - (bit & 7))) & 1) === 1;
        bit++;
      }
    }
    upward = !upward;
  }
}

const MASKS: ReadonlyArray<(x: number, y: number) => boolean> = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function applyMask(g: Grid, mask: number): void {
  const fn = MASKS[mask]!;
  for (let y = 0; y < g.size; y++) {
    for (let x = 0; x < g.size; x++) {
      if (!g.reserved[y]![x] && fn(x, y)) g.dark[y]![x] = !g.dark[y]![x];
    }
  }
}

/**
 * คะแนนโทษของหน้ากาก — ยิ่งน้อยยิ่งอ่านง่าย
 * เลือกหน้ากากที่ได้คะแนนต่ำสุดตามกติกา 4 ข้อของมาตรฐาน
 */
function penalty(g: Grid): number {
  const n = g.size;
  let score = 0;

  // ข้อ 1 · สีเดียวกันติดกันเกิน 5 ช่อง ทั้งแนวนอนและแนวตั้ง
  for (const vertical of [false, true]) {
    for (let a = 0; a < n; a++) {
      let run = 1;
      for (let b = 1; b < n; b++) {
        const cur = vertical ? g.dark[b]![a]! : g.dark[a]![b]!;
        const prev = vertical ? g.dark[b - 1]![a]! : g.dark[a]![b - 1]!;
        if (cur === prev) {
          run++;
          if (run === 5) score += 3;
          else if (run > 5) score += 1;
        } else run = 1;
      }
    }
  }

  // ข้อ 2 · บล็อกสีเดียวกันขนาด 2×2
  for (let y = 0; y < n - 1; y++) {
    for (let x = 0; x < n - 1; x++) {
      const v = g.dark[y]![x]!;
      if (v === g.dark[y]![x + 1] && v === g.dark[y + 1]![x] && v === g.dark[y + 1]![x + 1]) {
        score += 3;
      }
    }
  }

  // ข้อ 3 · ลายที่ชวนให้สับสนกับลายค้นหา
  const a = [true, false, true, true, true, false, true, false, false, false, false];
  const b = [false, false, false, false, true, false, true, true, true, false, true];
  const matches = (get: (i: number) => boolean, start: number, pat: boolean[]) =>
    pat.every((want, i) => get(start + i) === want);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= n - 11; j++) {
      const row = (k: number) => g.dark[i]![k]!;
      const col = (k: number) => g.dark[k]![i]!;
      if (matches(row, j, a) || matches(row, j, b)) score += 40;
      if (matches(col, j, a) || matches(col, j, b)) score += 40;
    }
  }

  // ข้อ 4 · สัดส่วนดำต่อขาวที่เบี่ยงจากครึ่งหนึ่ง
  let dark = 0;
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (g.dark[y]![x]) dark++;
  const percent = (dark * 100) / (n * n);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* ══ ประกอบทั้งหมด ════════════════════════════════════════════ */

/** เรียงบิตข้อมูลเป็น codeword พร้อมเติมช่องว่างตามที่มาตรฐานกำหนด */
function buildCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const capacity = dataCapacity(version);
  const bits: number[] = [];
  const push = (value: number, count: number) => {
    for (let i = count - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // โหมด byte
  push(bytes.length, countBits(version));
  for (const byte of bytes) push(byte, 8);

  // ตัวปิดท้าย ไม่เกิน 4 บิต และไม่เกินที่เหลืออยู่
  push(0, Math.min(4, capacity * 8 - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const out = new Uint8Array(capacity);
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!;
    out[i >> 3] = byte;
  }
  // เติมส่วนที่เหลือด้วยสองค่านี้สลับกัน ตามที่มาตรฐานกำหนด
  for (let i = bits.length / 8, alt = 0; i < capacity; i++, alt++) {
    out[i] = alt % 2 === 0 ? 0xec : 0x11;
  }
  return out;
}

/**
 * สลับ codeword ของทุกบล็อกเข้าด้วยกัน
 *
 * จุดประสงค์คือกระจายข้อมูลของแต่ละบล็อกให้อยู่คนละบริเวณของ QR
 * รอยเปื้อนจุดเดียวจึงกระทบหลายบล็อกเล็กน้อย แทนที่จะทำลายบล็อกเดียวจนหมด
 */
function interleave(data: Uint8Array, version: number): Uint8Array {
  const spec = SPEC_M[version - 1]!;
  const blocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];

  let offset = 0;
  for (const [count, size] of spec.groups) {
    for (let i = 0; i < count; i++) {
      const block = data.subarray(offset, offset + size);
      offset += size;
      blocks.push(block);
      ecBlocks.push(rsRemainder(block, spec.ec));
    }
  }

  const out: number[] = [];
  const longest = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < longest; i++) {
    for (const block of blocks) if (i < block.length) out.push(block[i]!);
  }
  for (let i = 0; i < spec.ec; i++) {
    for (const block of ecBlocks) out.push(block[i]!);
  }
  return new Uint8Array(out);
}

/**
 * ★ ช่องบอกความยาวกว้างไม่เท่ากันทุกเวอร์ชัน ★
 * โหมด byte ใช้ 8 บิตสำหรับเวอร์ชัน 1–9 และ 16 บิตตั้งแต่เวอร์ชัน 10 ขึ้นไป
 * คิดผิดตรงนี้ QR จะออกมาสวยงามแต่เครื่องสแกนอ่านความยาวได้เป็นศูนย์
 */
function countBits(version: number): number {
  return version < 10 ? 8 : 16;
}

/** เวอร์ชันที่เล็กที่สุดที่ใส่ข้อมูลจำนวนนี้ได้ */
function pickVersion(byteLength: number): number {
  for (let v = 1; v <= MAX_VERSION; v++) {
    const needed = 4 + countBits(v) + byteLength * 8;
    if (needed <= dataCapacity(v) * 8) return v;
  }
  throw new Error(
    `ข้อมูลยาว ${byteLength} ไบต์ เกินกว่าที่ QR เวอร์ชัน ${MAX_VERSION} รับได้`,
  );
}

/**
 * สร้าง QR จากข้อความ
 *
 * รับเฉพาะ ASCII เพราะ payload พร้อมเพย์เป็น ASCII ล้วนตามมาตรฐาน EMVCo
 * ถ้ามีอักขระอื่นหลุดเข้ามาแปลว่าต้นทางผิด ควรรู้ทันทีมากกว่าได้ QR ที่สแกนแล้วเพี้ยน
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code > 0x7f) {
      throw new Error(`QR ตัวนี้รองรับเฉพาะ ASCII แต่เจออักขระ "${text[i]}"`);
    }
    bytes[i] = code;
  }

  const version = pickVersion(bytes.length);
  const payload = interleave(buildCodewords(bytes, version), version);

  let best: Grid | null = null;
  let bestScore = Infinity;

  for (let mask = 0; mask < 8; mask++) {
    const g = newGrid(version);
    drawFunctionPatterns(g, version);
    placeData(g, payload);
    applyMask(g, mask);
    drawFormat(g, mask);
    drawVersion(g, version);

    const score = penalty(g);
    if (score < bestScore) {
      bestScore = score;
      best = g;
    }
  }

  const grid = best!;
  return { version, size: grid.size, modules: grid.dark };
}

/**
 * โมดูลที่ถูกจองไว้เป็นลายประจำตำแหน่ง — true = จองแล้ว เก็บข้อมูลไม่ได้
 * เปิดออกมาเพื่อให้ test ถอดรหัส QR กลับได้โดยไม่ต้องเดาว่าช่องไหนเป็นข้อมูล
 */
export function reservedGrid(version: number): boolean[][] {
  const g = newGrid(version);
  drawFunctionPatterns(g, version);
  return g.reserved;
}

/** จำนวนโมดูลที่ใช้เก็บข้อมูลได้จริงบนตาราง — ใช้ตรวจสอบตารางค่าคงที่ใน test */
export function freeModuleCount(version: number): number {
  return reservedGrid(version).reduce(
    (sum, row) => sum + row.filter((r) => !r).length,
    0,
  );
}

/** จำนวน codeword ทั้งหมดที่ตารางโครงสร้างบล็อกบอกไว้สำหรับเวอร์ชันนี้ */
export function specCodewordCount(version: number): number {
  const spec = SPEC_M[version - 1]!;
  const blocks = spec.groups.reduce((sum, [count]) => sum + count, 0);
  return dataCapacity(version) + blocks * spec.ec;
}
